"use client";
import React from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { cn } from "@/utils/utils";
import { Button } from "../input/button";
import { Input } from "../input/input";
import { Text } from "../information/text";
import {
  ChevronRightDouble,
  ChevronLeftDouble,
  ChevronRight,
  ChevronDown,
  SearchLg,
} from "@untitledui/icons";
import { styleDrivers } from "@/utils/componentStyle";

type SidebarItem = {
  id: string;
  label: React.ReactNode;
  href?: string;
  onClick?: () => void;
  action?: () => void;
  icon?: React.ReactNode | string;
  badge?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  searchTerms?: string[];
};

type SidebarSection = {
  id: string;
  title?: React.ReactNode;
  items: SidebarItem[];
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
};

export type SidebarProps = {
  position?: "left" | "right";
  /** Fixed width in pixels or any CSS size (e.g., 280, "18rem") */
  width?: number | string;
  /** Start collapsed */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state */
  collapsed?: boolean;
  /** Controlled setter */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Render header content */
  header?: React.ReactNode;
  /** Render logo */
  logo?: React.ReactNode;
  /** Render footer content */
  footer?: React.ReactNode;
  /** Optional top slot above sections */
  topSlot?: React.ReactNode;
  /** Optional bottom slot below sections */
  bottomSlot?: React.ReactNode;
  /** Navigation sections */
  sections?: SidebarSection[];
  /** Class for the outer wrapper */
  className?: string;
  /** Class for the inner panel */
  panelClassName?: string;
  /** Class for the nav */
  navClassName?: string;
  /** Hide the collapse toggle */
  hideToggle?: boolean;
  /** Enable search functionality */
  searchable?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Custom search handler */
  onSearch?: (query: string) => void;
  /** Custom collapse icon (when section is expanded) */
  collapseIcon?: React.ReactNode | string;
  /** Custom expand icon (when section is collapsed) */
  expandIcon?: React.ReactNode | string;
  /** High-level navigation config; if provided, it will be transformed into sections */
  navigation?: SidebarNavigationGroup[];
};

export type SidebarNavigationItem = {
  id?: string;
  label: string;
  href?: string;
  searchTerms?: string[];
  disabled?: boolean;
  badge?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode | string;
};

export type SidebarNavigationSubsection = {
  id?: string;
  title: string;
  items: SidebarNavigationItem[];
  defaultCollapsed?: boolean;
  icon?: React.ReactNode | string;
};

export type SidebarNavigationGroup = {
  id?: string;
  title: string;
  subsections: SidebarNavigationSubsection[];
};

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export function Sidebar(props: SidebarProps) {
  const {
    position = "left",
    width = 280,
    defaultCollapsed = false,
    collapsed: collapsedProp,
    onCollapsedChange,
    header,
    logo,
    footer,
    topSlot,
    bottomSlot,
    sections = [],
    className,
    panelClassName,
    navClassName,
    hideToggle,
    searchable = false,
    searchPlaceholder = "Search...",
    onSearch,
    collapseIcon = <ChevronDown className="h-4 w-4" />,
    expandIcon = <ChevronRight className="h-4 w-4" />,
    navigation,
  } = props;

  const [uncontrolledCollapsed, setUncontrolledCollapsed] =
    React.useState(defaultCollapsed);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sectionCollapseStates, setSectionCollapseStates] = React.useState<
    Record<string, boolean>
  >({});

  // Arrow key navigation state
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const itemRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const navRef = React.useRef<HTMLElement>(null);

  const isControlled = typeof collapsedProp === "boolean";
  const collapsed = isControlled
    ? (collapsedProp as boolean)
    : uncontrolledCollapsed;

  const setCollapsed = (value: boolean) => {
    if (!isControlled) setUncontrolledCollapsed(value);
    onCollapsedChange?.(value);
  };

  const toggleSectionCollapse = (sectionId: string) => {
    setSectionCollapseStates((prev) => {
      const section = sections.find((s) => s.id === sectionId);
      const currentState =
        prev[sectionId] ?? section?.defaultCollapsed ?? false;
      return {
        ...prev,
        [sectionId]: !currentState,
      };
    });
  };

  const filterItems = (items: SidebarItem[], query: string): SidebarItem[] => {
    if (!query.trim()) return items;

    const searchLower = query.toLowerCase();
    return items.filter((item) => {
      const labelMatch =
        typeof item.label === "string"
          ? item.label.toLowerCase().includes(searchLower)
          : false;
      const searchTermsMatch =
        item.searchTerms?.some((term) =>
          term.toLowerCase().includes(searchLower),
        ) || false;
      return labelMatch || searchTermsMatch;
    });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch?.(query);
  };

  const renderIcon = React.useCallback((icon: React.ReactNode | string) => {
    if (typeof icon === "string") {
      return <div className={icon} />;
    }
    return icon;
  }, []);

  const location = useLocation();
  const parsedWidth = typeof width === "number" ? `${width}px` : width;
  const [currentHash, setCurrentHash] = React.useState<string>("");

  // keep currentHash in sync with URL hash changes
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const onHashChange = () => setCurrentHash(window.location.hash);
    // set initial hash on mount to avoid SSR/client mismatch
    onHashChange();
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Helper function to create title JSX elements (called during render, not in memoized value)
  const createTitleElement = React.useCallback(
    (sub: { title: string; icon?: React.ReactNode | string }) => (
      <div className="flex items-center gap-2">
        {sub.icon && (
          <div className={cn(styleDrivers.sidebar.itemIcon)}>
            {renderIcon(sub.icon)}
          </div>
        )}
        <Text as="p">{sub.title}</Text>
      </div>
    ),
    [renderIcon],
  );

  // Store subsection metadata separately to avoid creating JSX in memoized value
  const subsectionMetadata = React.useMemo(() => {
    if (!navigation || navigation.length === 0) return null;
    return navigation.flatMap((group) =>
      group.subsections.map((sub) => ({
        groupId: group.id ?? toSlug(group.title),
        subsectionId: sub.id ?? toSlug(sub.title),
        title: sub.title,
        icon: sub.icon,
        defaultCollapsed: sub.defaultCollapsed,
      })),
    );
  }, [navigation]);

  // If a high-level navigation config is provided, transform it into sections
  // Store title data instead of JSX to prevent infinite re-renders
  const derivedSectionsData = React.useMemo(() => {
    if (!navigation || navigation.length === 0) return null;
    if (!subsectionMetadata) return null;

    const mapped: Array<{
      id: string;
      titleData: { title: string; icon?: React.ReactNode | string } | null;
      groupTitle?: string; // For group header sections
      items: SidebarItem[];
      collapsible: boolean;
      defaultCollapsed?: boolean;
      isGroupHeader?: boolean;
    }> = [];
    let metadataIndex = 0;
    navigation.forEach((group) => {
      const groupId = group.id ?? toSlug(group.title);

      // Add a group header section (non-collapsible, no items)
      mapped.push({
        id: `${groupId}-header`,
        titleData: null,
        groupTitle: group.title,
        items: [],
        collapsible: false,
        isGroupHeader: true,
      });

      group.subsections.forEach((sub) => {
        const meta = subsectionMetadata[metadataIndex++];
        if (!meta) return;

        const sectionId = `${meta.groupId}-${meta.subsectionId}`;
        const items: SidebarItem[] = sub.items.map((it) => ({
          id: it.id ?? toSlug(it.label),
          label: it.label,
          href: it.href,
          searchTerms: it.searchTerms,
          disabled: it.disabled,
          badge: it.badge,
          className: it.className,
          icon: it.icon,
        }));

        mapped.push({
          id: sectionId,
          titleData: { title: meta.title, icon: meta.icon },
          items,
          collapsible: true,
          defaultCollapsed: meta.defaultCollapsed,
        });
      });
    });
    return mapped;
  }, [navigation, subsectionMetadata]);

  // Convert data to sections - create titles during render to avoid infinite loops
  // We'll handle title creation in the render function instead
  const derivedSections: (SidebarSection & { isGroupHeader?: boolean; groupTitle?: string })[] = React.useMemo(() => {
    if (!derivedSectionsData) return sections;
    // Store title data instead of JSX to keep the array stable
    return derivedSectionsData.map((data) => ({
      id: data.id,
      // Store the data needed to create the title, not the JSX itself
      title: data.titleData as any, // We'll handle this in render
      items: data.items,
      collapsible: data.collapsible,
      defaultCollapsed: data.defaultCollapsed,
      isGroupHeader: data.isGroupHeader,
      groupTitle: data.groupTitle,
    }));
  }, [derivedSectionsData, sections]);

  // Memoize effectiveSections to prevent infinite re-renders
  const effectiveSections = React.useMemo(() => {
    return navigation && navigation.length > 0 ? derivedSections : sections;
  }, [navigation, derivedSections, sections]);

  // Initialize section collapse states when effectiveSections change
  // Use a ref to track if we've already initialized to prevent infinite loops
  const sectionsInitializedRef = React.useRef<Set<string>>(new Set());
  const previousSectionsKeyRef = React.useRef<string>("");

  // Create a stable dependency based on section IDs and their defaultCollapsed values
  // This avoids depending on the entire effectiveSections array which changes due to JSX
  const sectionsKey = React.useMemo(() => {
    return effectiveSections
      .map((s) => `${s.id}:${s.defaultCollapsed ?? false}`)
      .join("|");
  }, [effectiveSections]);

  React.useEffect(() => {
    // Only run if sectionsKey actually changed (not just reference change)
    if (previousSectionsKeyRef.current === sectionsKey) {
      return;
    }
    previousSectionsKeyRef.current = sectionsKey;

    // Only update states for new sections that haven't been initialized
    const currentSections =
      navigation && navigation.length > 0 ? derivedSections : sections;

    setSectionCollapseStates((prev) => {
      const newStates = { ...prev };
      let hasChanges = false;

      currentSections.forEach((section) => {
        if (
          section.collapsible &&
          section.defaultCollapsed !== undefined &&
          !(section.id in prev) &&
          !sectionsInitializedRef.current.has(section.id)
        ) {
          newStates[section.id] = section.defaultCollapsed;
          sectionsInitializedRef.current.add(section.id);
          hasChanges = true;
        }
      });

      // Only return new object if there are actual changes
      return hasChanges ? newStates : prev;
    });
  }, [sectionsKey, navigation, derivedSections, sections]);

  // Get all navigable items as a flat list for keyboard navigation
  const getNavigableItems = React.useCallback((): { id: string; sectionId: string }[] => {
    const items: { id: string; sectionId: string }[] = [];
    effectiveSections.forEach((section) => {
      const extSection = section as typeof section & { isGroupHeader?: boolean };
      if (extSection.isGroupHeader) return;

      const isSectionCollapsed =
        section.collapsible &&
        (sectionCollapseStates[section.id] ?? section.defaultCollapsed ?? false);

      if (isSectionCollapsed) return;

      const filteredItems = filterItems(section.items, searchQuery);
      filteredItems.forEach((item) => {
        if (!item.disabled) {
          items.push({ id: item.id, sectionId: section.id });
        }
      });
    });
    return items;
  }, [effectiveSections, sectionCollapseStates, searchQuery]);

  // Handle keyboard navigation
  const handleNavKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const navigableItems = getNavigableItems();
      if (navigableItems.length === 0) return;

      let newIndex = focusedIndex;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          newIndex = focusedIndex < navigableItems.length - 1 ? focusedIndex + 1 : 0;
          break;
        case "ArrowUp":
          e.preventDefault();
          newIndex = focusedIndex > 0 ? focusedIndex - 1 : navigableItems.length - 1;
          break;
        case "Home":
          e.preventDefault();
          newIndex = 0;
          break;
        case "End":
          e.preventDefault();
          newIndex = navigableItems.length - 1;
          break;
        case "Enter":
        case " ":
          if (focusedIndex >= 0 && focusedIndex < navigableItems.length) {
            const focusedItem = navigableItems[focusedIndex];
            const element = itemRefs.current.get(focusedItem.id);
            if (element) {
              e.preventDefault();
              const link = element.querySelector("a") || element.querySelector("[role='button']");
              if (link) {
                (link as HTMLElement).click();
              } else {
                element.click();
              }
            }
          }
          return;
        default:
          return;
      }

      setFocusedIndex(newIndex);
      const targetItem = navigableItems[newIndex];
      if (targetItem) {
        const element = itemRefs.current.get(targetItem.id);
        element?.focus();
      }
    },
    [focusedIndex, getNavigableItems]
  );

  // Reset focused index when search query changes
  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [searchQuery]);

  // Memoized index map for efficient lookup during render
  const navigableItemsIndexMap = React.useMemo(() => {
    const items = getNavigableItems();
    const map = new Map<string, number>();
    items.forEach((item, idx) => map.set(item.id, idx));
    return map;
  }, [getNavigableItems]);

  // Separate effect for intersection observer to avoid triggering state updates
  React.useEffect(() => {
    const hashIds = new Set<string>();
    effectiveSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.href && item.href.includes("#")) {
          const id = item.href.split("#")[1];
          if (id) hashIds.add(id);
        }
      });
    });

    if (hashIds.size === 0 || typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) =>
            a.boundingClientRect.top > b.boundingClientRect.top ? 1 : -1,
          );
        if (visible[0]?.target?.id) {
          setCurrentHash(`#${visible[0].target.id}`);
        }
      },
      {
        root: null,
        rootMargin: "0px 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    const elements: Element[] = [];
    hashIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        elements.push(el);
      }
    });

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [effectiveSections]);

  return (
    <aside
      className={cn(
        styleDrivers.sidebar.root,
        position === "left" ? "border-r" : "border-l",
        className,
      )}
      style={{ width: collapsed ? "" : parsedWidth }}
    >
      <div className={cn(styleDrivers.sidebar.panel, panelClassName)}>
        <div
          className={cn(
            styleDrivers.sidebar.headerRow,
            collapsed ? "flex-col justify-center" : "justify-between",
          )}
        >
          <div className="flex items-center space-x-2 px-2">
            <div>{logo}</div>
            {!collapsed && (
              <div className={cn(styleDrivers.sidebar.headerTitle)}>
                {header}
              </div>
            )}
          </div>
          {!hideToggle && (
            <Button
              variant="text"
              size="icon"
              className={cn(styleDrivers.sidebar.toggleButton)}
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <ChevronRightDouble className="h-4 w-4" />
              ) : (
                <ChevronLeftDouble className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {searchable && !collapsed && (
          <div className={cn(styleDrivers.sidebar.searchContainer)}>
            <div className={cn(styleDrivers.sidebar.searchWrapper)}>
              <SearchLg className={cn(styleDrivers.sidebar.searchIcon)} />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className={cn(styleDrivers.sidebar.searchInput)}
              />
            </div>
          </div>
        )}

        {topSlot && (
          <div
            className={cn(
              styleDrivers.sidebar.topSlotContainer,
              collapsed && "opacity-0 pointer-events-none",
            )}
          >
            {topSlot}
          </div>
        )}

        <nav
          ref={navRef}
          className={cn(styleDrivers.sidebar.nav, navClassName)}
          onKeyDown={handleNavKeyDown}
          role="navigation"
          aria-label="Sidebar navigation"
        >
          {effectiveSections.map((section) => {
            // Type assertion to handle extended section type
            const extSection = section as typeof section & { isGroupHeader?: boolean; groupTitle?: string };

            // Render group headers differently
            if (extSection.isGroupHeader && extSection.groupTitle) {
              if (collapsed) return null;
              return (
                <div
                  key={section.id}
                  className={cn(
                    "px-3 pt-6 pb-2 first:pt-2",
                    section.className,
                  )}
                >
                  <Text
                    as="p"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {extSection.groupTitle}
                  </Text>
                </div>
              );
            }

            const filteredItems = filterItems(section.items, searchQuery);
            const sectionHasActive = filteredItems.some((item) => {
              if (!item.href) return false;
              const [itemPath, itemHash] = item.href.split("#");
              const matchesPath = itemPath
                ? location.pathname === itemPath ||
                  location.pathname.startsWith(itemPath)
                : true;
              const matchesHash = itemHash
                ? currentHash === `#${itemHash}`
                : true;
              return matchesPath && matchesHash;
            });
            const isSectionCollapsed =
              section.collapsible &&
              (sectionCollapseStates[section.id] ??
                section.defaultCollapsed ??
                false);

            // Hide section if no items match search
            if (searchQuery && filteredItems.length === 0) {
              return null;
            }

            return (
              <div
                key={section.id}
                className={cn(styleDrivers.sidebar.section, section.className)}
              >
                {section.title && (
                  <div
                    className={cn(
                      styleDrivers.sidebar.sectionTitleRow,
                      collapsed && "opacity-0 pointer-events-none",
                      section.collapsible &&
                        styleDrivers.sidebar.sectionTitleInteractive,
                    )}
                    onClick={
                      section.collapsible
                        ? () => toggleSectionCollapse(section.id)
                        : undefined
                    }
                  >
                    {section.title && !collapsed && (
                      <div
                        className={cn(styleDrivers.sidebar.sectionTitleText)}
                      >
                        {typeof section.title === "string" ||
                        typeof section.title === "number" ? (
                          <Text as="p">{section.title}</Text>
                        ) : typeof section.title === "object" &&
                          section.title !== null &&
                          "title" in section.title ? (
                          // Handle title data object from derivedSections
                          createTitleElement(
                            section.title as {
                              title: string;
                              icon?: React.ReactNode | string;
                            },
                          )
                        ) : (
                          section.title
                        )}
                      </div>
                    )}
                    {section.collapsible && !collapsed && (
                      <div
                        className={cn(styleDrivers.sidebar.expandIconWrapper)}
                      >
                        {isSectionCollapsed
                          ? renderIcon(expandIcon)
                          : renderIcon(collapseIcon)}
                      </div>
                    )}
                  </div>
                )}
                {(!section.collapsible || !isSectionCollapsed) && (
                  <ul
                    className={cn(styleDrivers.sidebar.itemsList)}
                    role="list"
                  >
                    {filteredItems.map((item) => {
                      const handleClick = (e?: React.MouseEvent) => {
                        if (item.disabled) {
                          e?.preventDefault();
                          e?.stopPropagation();
                          return;
                        }
                        item.onClick?.();
                        item.action?.();
                      };

                      const isActive =
                        !!item.href &&
                        (() => {
                          const [itemPath, itemHash] = item.href.split("#");
                          const matchesPath = itemPath
                            ? location.pathname === itemPath
                            : true;
                          const matchesHash = itemHash
                            ? currentHash === `#${itemHash}`
                            : true;
                          return matchesPath && matchesHash;
                        })();
                      const content = (
                        <div
                          className={cn(
                            styleDrivers.sidebar.itemRow,
                            isActive &&
                              "font-semibold text-primary bg-primary/[0.18] border-transparent ring-transparent",
                            item.disabled &&
                              styleDrivers.sidebar.itemRowDisabled,
                            item.className,
                          )}
                          onClick={!item.href ? handleClick : undefined}
                          aria-current={isActive ? "page" : undefined}
                        >
                          {item.icon && (
                            <div
                              className={cn(
                                styleDrivers.sidebar.itemIcon,
                                isActive && "text-primary",
                              )}
                            >
                              {renderIcon(item.icon)}
                            </div>
                          )}
                          <div
                            className={cn(
                              styleDrivers.sidebar.itemLabel,
                              isActive && "text-primary font-bold",
                              collapsed && "hidden",
                            )}
                          >
                            {typeof item.label === "string" ||
                            typeof item.label === "number" ? (
                              <Text as="p">{String(item.label)}</Text>
                            ) : (
                              item.label
                            )}
                          </div>
                          {item.badge && (
                            <div
                              className={cn(
                                styleDrivers.sidebar.itemBadgeWrapper,
                                collapsed && "hidden",
                              )}
                            >
                              {item.badge}
                            </div>
                          )}
                        </div>
                      );

                      // Get item index for keyboard navigation from memoized map
                      const itemIndex = navigableItemsIndexMap.get(item.id) ?? -1;
                      const isFocused = itemIndex >= 0 && itemIndex === focusedIndex;

                      return (
                        <li
                          key={item.id}
                          ref={(el) => {
                            if (el) {
                              itemRefs.current.set(item.id, el);
                            } else {
                              itemRefs.current.delete(item.id);
                            }
                          }}
                          tabIndex={item.disabled ? -1 : 0}
                          onFocus={() => setFocusedIndex(itemIndex)}
                          className={cn(
                            "outline-none focus-visible:bg-sidebar-accent rounded-theme-lg",
                          )}
                        >
                          {item.href ? (
                            <Link
                              to={item.href}
                              className="block outline-none"
                              tabIndex={-1}
                              onClick={(e) => {
                                if (item.disabled) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  return;
                                }
                                // Ensure navigation happens - don't prevent default
                                // Call custom handlers but ensure they don't block navigation
                                const runHandlers = () => {
                                  try {
                                    item.onClick?.();
                                    item.action?.();
                                  } catch (error) {
                                    console.error(
                                      "Error in sidebar item handler:",
                                      error,
                                    );
                                  }
                                };

                                // Run handlers after a microtask to ensure navigation proceeds
                                Promise.resolve().then(runHandlers);
                              }}
                            >
                              {content}
                            </Link>
                          ) : (
                            <div
                              onClick={handleClick}
                              role="button"
                              tabIndex={-1}
                              className="outline-none"
                            >
                              {content}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {bottomSlot && (
          <div
            className={cn(
              styleDrivers.sidebar.bottomSlotWrapper,
              collapsed && "opacity-0 pointer-events-none",
            )}
          >
            {bottomSlot}
          </div>
        )}
        {footer && (
          <div
            className={cn(
              styleDrivers.sidebar.footerWrapper,
              collapsed && "hidden pointer-events-none",
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </aside>
  );
}

export type SidebarLayoutProps = {
  sidebar: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Whether sidebar is fully collapsed (hidden, not just icon mode) */
  isCollapsed?: boolean;
  /** Content for topbar when collapsed (typically logo + toggle button) */
  collapsedTopbar?: React.ReactNode;
  /** Sidebar content for hover popup when collapsed */
  popupSidebar?: React.ReactNode;
  /** Popup state: hidden, visible, or closing (for exit animation) */
  popupState?: "hidden" | "visible" | "closing";
  /** Callback when mouse enters popup trigger or popup itself */
  onPopupMouseEnter?: () => void;
  /** Callback when mouse leaves popup */
  onPopupMouseLeave?: () => void;
};

export function SidebarLayout({
  sidebar,
  children,
  className,
  isCollapsed = false,
  collapsedTopbar,
  popupSidebar,
  popupState = "hidden",
  onPopupMouseEnter,
  onPopupMouseLeave,
}: SidebarLayoutProps) {
  const showPopup = popupState === "visible" || popupState === "closing";

  return (
    <div className={cn("h-full overflow-hidden relative", className)}>
      {/* Collapsed topbar with logo + toggle */}
      {isCollapsed && collapsedTopbar && (
        <div className={styleDrivers.sidebar.collapsedTopbar}>
          <div className={styleDrivers.sidebar.collapsedTopbarContent}>
            {collapsedTopbar}
          </div>
        </div>
      )}

      {/* Main content area */}
      <div
        className={cn(
          "overflow-auto scrollbar-hidden grid transition-all duration-300",
          isCollapsed
            ? "h-[calc(100%-3.5rem)] grid-cols-1"
            : "h-full grid-cols-[auto_1fr]",
        )}
      >
        {/* Regular sidebar (hidden when collapsed) */}
        {!isCollapsed && (
          <div className="h-full overflow-auto scrollbar-hidden">{sidebar}</div>
        )}

        {/* Main content */}
        <div className="h-full overflow-auto scrollbar-hidden">{children}</div>
      </div>

      {/* Popup sidebar overlay (shown on hover when collapsed) */}
      {isCollapsed && showPopup && popupSidebar && (
        <div
          className={styleDrivers.sidebar.popupOverlay}
          onMouseEnter={onPopupMouseEnter}
          onMouseLeave={onPopupMouseLeave}
        >
          <div
            className={cn(
              styleDrivers.sidebar.popupContainer,
              popupState === "closing" && "animate-sidebar-out",
            )}
          >
            {popupSidebar}
          </div>
        </div>
      )}
    </div>
  );
}

